import {RecordsWebPage} from '@fe-progressive/package-progressive/records-web';
import '@fe-progressive/package-progressive/records-web/style.css';
import {
  type ProgressiveRecordItem,
  type ProgressiveRecordType,
  normalizeRecordStatus,
  normalizeWithdrawRecordStatus,
} from '@fe-progressive/package-progressive';
import globalStore from '@/services/global.state';
import {copyText, goBack, goCS, toPriceStr} from '@/utils';
import dayjs from 'dayjs';
import React from 'react';
import {
  getRechargeLogs,
  getTransferLogs,
  getWithdrawLogs,
  type RechargeLogItem,
  type TransferLogItem,
  type WithdrawLogItem,
} from './records-service';

function formatAmount(amount: number) {
  return toPriceStr(amount, {
    thousands: true,
    spacing: true,
    currency: globalStore.currency,
  });
}

function mapWithdraw(item: WithdrawLogItem): ProgressiveRecordItem {
  return {
    id: item.orderNo,
    type: 'withdraw',
    status: normalizeWithdrawRecordStatus(item.respCode),
    amount: item.paidAmount,
    reward: item.reward,
    fee: item.withdrawFeeRate * item.paidAmount,
    orderNo: item.orderNo,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    bankSummary: {
      bankName: item.bankName,
      userCardNumber: item.userCardNumber,
    },
    crypto: {
      payCategory: item.payCategory,
      amount: item.systemSupportAmount,
      address: item.payAddress,
    },
    raw: item,
  };
}

function mapRecharge(item: RechargeLogItem): ProgressiveRecordItem {
  return {
    id: item.orderNo,
    type: 'recharge',
    status: normalizeRecordStatus(item.status),
    amount: item.paidAmount,
    reward: item.reward,
    channelName: item.channelName,
    orderNo: item.orderNo,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    crypto: {
      payCategory: item.payCategory,
    },
    raw: item,
  };
}

function mapTransfer(item: TransferLogItem): ProgressiveRecordItem {
  return {
    id: item.order,
    type: 'transfer',
    amount: item.price,
    reward: item.reward,
    orderNo: item.order,
    createdAt: item.appDate || item.createTime,
    balance: item.balance,
    raw: item,
  };
}

async function fetchRecords(
  type: ProgressiveRecordType,
  pageNo: number,
  pageSize: number,
) {
  if (type === 'withdraw') {
    return (await getWithdrawLogs(pageNo, pageSize)).map(mapWithdraw);
  }
  if (type === 'recharge') {
    return (await getRechargeLogs(pageNo, pageSize)).map(mapRecharge);
  }
  return (await getTransferLogs(pageNo, pageSize)).map(mapTransfer);
}

export default function RecordsProgressive(props: {
  type: ProgressiveRecordType;
}) {
  const {type} = props;
  const adapter = React.useMemo(
    () => ({
      fetchRecords: ({pageNo, pageSize}: {pageNo: number; pageSize: number}) =>
        fetchRecords(type, pageNo, pageSize),
      copyText: (text: string) => copyText(text),
      showToast: (notice: {message: string}) =>
        globalStore.globalSucessTotal(notice.message),
    }),
    [type],
  );

  return (
    <RecordsWebPage
      type={type}
      adapter={adapter}
      onBack={goBack}
      onCustomerService={goCS}
      formatAmount={formatAmount}
      formatTime={(time?: number) =>
        time ? dayjs(Number(time)).format('DD/MM/YYYY hh:mm') : ''
      }
    />
  );
}
